import { getConfigValue } from '@jutro/config';

const tokenMap = new Map();
const RECAPTCHA_SCRIPT_ID = 'wmic-recaptcha-v3-script';
const DEFAULT_CAPTCHA_TIMEOUT = 110000;
const DEFAULT_CAPTCHA_TOKEN_RETRY_MAX_ATTEMPTS = 3;
const DEFAULT_CAPTCHA_TOKEN_RETRY_TIMEOUT = 500;

export default class WMICReCaptchaService {

    static async ensureReCaptchaScriptLoaded(captchaApiKey) {
        return new Promise((resolve, reject) => {
            if (!captchaApiKey || captchaApiKey === '{{CAPTCHA_API_KEY}}') {
                reject('CAPTCHA_API_KEY is not configured.');
                return;
            }

            if (typeof globalThis.grecaptcha !== 'undefined') {
                resolve(true);
                return;
            }

            const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID);
            if (existingScript) {
                existingScript.addEventListener('load', () => resolve(true), { once: true });
                existingScript.addEventListener('error', () => reject('Failed to load reCAPTCHA script.'), { once: true });
                return;
            }

            const script = document.createElement('script');
            script.id = RECAPTCHA_SCRIPT_ID;
            script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(captchaApiKey)}`;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve(true);
            script.onerror = () => reject('Failed to load reCAPTCHA script.');
            document.head.appendChild(script);
        });
    }

    static async waitForReCaptchaToBeLoaded(captchaApiKey) {
        return this.ensureReCaptchaScriptLoaded(captchaApiKey).then(() => {
            return new Promise((resolve, reject) => {
                const timerId = setInterval(() => {
                    if (typeof globalThis.grecaptcha !== 'undefined') {
                        resolve(true);
                        clearInterval(timerId);
                    }
                }, 1000);

                // after 20 seconds stop
                setTimeout(() => {
                    clearInterval(timerId);
                    if (typeof globalThis.grecaptcha === 'undefined') {
                        reject('reCAPTCHA did not become available.');
                    }
                }, 20000);
            });
        });
    };

    static async fetchReCaptchaToken(method, overrideCachedToken=false) {
        const currentTime = Date.now();
        const captchaTimeout = getConfigValue('captchaConfig.timeout', DEFAULT_CAPTCHA_TIMEOUT);
        const tokenRetrievalRetryMaxAttempts = getConfigValue(
            'captchaConfig.tokenRetrievalRetryMaxAttempts',
            DEFAULT_CAPTCHA_TOKEN_RETRY_MAX_ATTEMPTS
        );
        const tokenRetrievalRetryTimeout = getConfigValue(
            'captchaConfig.tokenRetrievalRetryTimeout',
            DEFAULT_CAPTCHA_TOKEN_RETRY_TIMEOUT
        );
        const cachedToken = tokenMap.get(method);
        if (cachedToken && currentTime - cachedToken.timeStamp < captchaTimeout && !overrideCachedToken) {
            return cachedToken.token;
        }

        return new Promise(((resolve, reject) => {
            const captchaApiKey = getConfigValue('CAPTCHA_API_KEY');

            function getToken(captchaApiKey, method, resolve, reject, currentTime, currentAttempt) {
                const reCaptcha = globalThis.grecaptcha;
                if (!reCaptcha) {
                    reject('grecaptcha is not available.');
                    return;
                }

                reCaptcha.execute(captchaApiKey, {action: method})
                    .then((token) => {
                        resolve(token);
                        tokenMap.set(method, {token, timeStamp: currentTime});
                        return token;
                    }, (error) => {
                        if (currentAttempt + 1 > tokenRetrievalRetryMaxAttempts) {
                            reject(`grecaptcha.execute failed. Details: ${error}`);
                        } else {
                            // grecaptcha.execute failed for some reason. There is very little documentation about
                            // why this would fail and the error is null. The best course of action is to retry
                            // the execute operation after a short timeout.
                            setTimeout(getToken, tokenRetrievalRetryTimeout, captchaApiKey, method, resolve, reject, currentTime, currentAttempt + 1);
                        }
                    });
            }

            return this.waitForReCaptchaToBeLoaded(captchaApiKey).then(() => {
                const reCaptcha = globalThis.grecaptcha;
                if (!reCaptcha) {
                    reject('grecaptcha is not available.');
                    return;
                }

                return reCaptcha.ready(() => {
                    getToken(captchaApiKey, method, resolve, reject, currentTime, 1)
                });
            });
        }));
    }
}